import type {
  TpStripePremiumYearDiscountInfo,
  TpStripePremiumYearInfo,
} from '@features/Stripe/types';
import { atom } from 'jotai';
import { atomWithReset, atomWithStorage, createJSONStorage } from 'jotai/utils';

export const userAtom = atomWithStorage('stripeUser', {
  ...createJSONStorage(() => sessionStorage),
  customerId: '',
  email: '',
  token: '',
  campaign: '',
  userId: 0,
  accessCode: '',
});

type TpSelectedPlanAtom = {
  bundle?: boolean;
  academy_price_id: string | null;
  currency: string;
  planName: string;
  discountPercentage: number;
  fullPrice: number;
  fullPriceDiscount: number;
  billingPeriod: number;
  billingPeriodUnit: string;
  specialDiscountPercentage: number;
  priceId: string;
  introOffer: string;
  braintreePlanId: undefined | string;
  braintreeMerchantId: undefined | string;
  specialOffer: boolean;
  shopify?: boolean;
  premiumDiscountPriceData?: {
    upgradeId: string;
    braintreePlanId: null | string;
    discountPercentage: number;
    fullPriceDiscount: number;
    fullPrice: number;
    currency: string;
    introOffer: string;
    introOfferData: {
      planId: string;
      billingPeriod: number;
      billingPeriodUnit: string;
      currency: string;
      introOfferId: string;
      introOfferDuration: string;
      fullPriceDiscount: number;
      fractionalPriceDiscount: number;
      fractionalPrice: number;
      fullPrice: number;
      displayPrice: string;
      discountPercentage: number;
      introOfferDescription: null | string;
      introOfferMustBePaid: null;
      braintreePlanId: string;
    };
  };
  premiumPriceData?: {
    upgradeId: string;
    braintreePlanId: null | string;
    discountPercentage: number;
    fullPriceDiscount: number;
    fullPrice: number;
    currency: string;
    introOffer: string;
    introOfferData: {
      planId: string;
      billingPeriod: number;
      billingPeriodUnit: string;
      currency: string;
      introOfferId: string;
      introOfferDuration: string;
      fullPriceDiscount: number;
      fractionalPriceDiscount: number;
      fractionalPrice: number;
      fullPrice: number;
      displayPrice: string;
      discountPercentage: number;
      introOfferDescription: null | string;
      introOfferMustBePaid: null;
      braintreePlanId: string;
    };
  };
  introOfferData: {
    fullPrice: string;
    fullPriceDiscount: string;
    currency: string;
    introOfferDescription: string;
    introOfferDuration: string;
    introOfferId: string;
    billingPeriod: number;
    billingPeriodUnit: string;
  };
};

export const selectedPlanAtom = atomWithReset<TpSelectedPlanAtom>({
  academy_price_id: null,
  bundle: false,
  currency: 'usd',
  planName: '',
  discountPercentage: 0,
  fullPrice: 0,
  fullPriceDiscount: 0,
  shopify: false,
  billingPeriod: 0,
  billingPeriodUnit: '',
  specialDiscountPercentage: 0,
  priceId: '',
  introOffer: '',
  braintreePlanId: undefined,
  braintreeMerchantId: undefined,
  specialOffer: false,
  introOfferData: {
    fullPrice: '0',
    fullPriceDiscount: '0',
    introOfferDescription: '',
    currency: 'usd',
    introOfferDuration: '',
    introOfferId: '',
    billingPeriod: 0,
    billingPeriodUnit: '',
  },
});

export const upsellInfoAtom = atomWithStorage('stripeUpsellInfo', {
  ...createJSONStorage(() => sessionStorage),
  currency: 'USD',
  discountPercentage: 0,
  fullPrice: 0,
  fullPriceDiscount: 0,
});

export const premiumYearAllDataAtom = atomWithStorage<{
  premiumYearData: TpStripePremiumYearInfo;
  premiumYearDiscountData: TpStripePremiumYearDiscountInfo;
}>('stripePremiumYearData', {
  ...createJSONStorage(() => sessionStorage),
  premiumYearData: {
    currency: 'usd',
    fullPriceDiscount: 0,
    fullPrice: 0,
    discountPercentage: 0,
    braintreePlanId: '',
    upgradeId: '',
  },
  premiumYearDiscountData: {
    currency: 'usd',
    fullPriceDiscount: 0,
    fullPrice: 0,
    discountPercentage: 0,
    braintreePlanId: '',
    upgradeId: '',
  },
});

export const upgradeInfoAtom = atomWithStorage('stripeUpgradeInfo', {
  ...createJSONStorage(() => sessionStorage),
  currency: 'USD',
  discountPercentage: 0,
  fullPrice: 0,
  fullPriceDiscount: 0,
  upgradeId: '',
  braintreePlanId: '',
});

export const introOfferInfoAtom = atomWithStorage('stripeIntroOfferInfo', {
  ...createJSONStorage(() => sessionStorage),
  currency: 'usd',
  fullPriceDiscount: 0,
  introOfferDuration: 0,
  introOfferId: '',
  planId: '',
  billingPeriodUnit: '',
  billingPeriod: 0,
});

export const processingModalStatusAtom = atom(false);

export const stripeWasPaymentTriedAtom = atomWithStorage(
  'stripeWasPaymentTried',
  {
    ...createJSONStorage(() => sessionStorage),
    showCrossSailOffer: false,
    wasUpsellSailOfferShown: false,
  },
);

export const stripePaymentErrorAtom = atomWithStorage('stripePaymentError', {
  ...createJSONStorage(() => sessionStorage),
  wasPaymentDeclinedOfferAccepted: false,
  errorCode: '',
  declineCode: '',
  paymentSystem: '',
  paymentMethod: '',
});

export const stripePaymentRetryAtom = atomWithStorage(
  'stripePaymentRetry',
  {
    retryModalShownCount: 0,
    retryPaymentErrorKey: '',
    sameRetryablePaymentErrorRetryCount: 0,
  },
  createJSONStorage(() => sessionStorage),
);

export const stripeTryAnotherCardAtom = atomWithStorage(
  'stripeTryAnotherCard',
  {
    showModal: false,
    planId: '',
    paymentMethod: '',
    tryAnotherCardModalShownCount: 0,
    tryAnotherCardTappedCount: 0,
    clearCardFormCount: 0,
  },
  createJSONStorage(() => sessionStorage),
);

export const stripePaymentDeclinedOfferAtom = atomWithStorage(
  'stripePaymentDeclinedOffer',
  {
    paymentDeclinedShownCount: 0,
    paymentDeclinedTappedCount: 0,
  },
  createJSONStorage(() => sessionStorage),
);

export const userPaymentsInfoAtom = atomWithStorage('stripePaymentError', {
  ...createJSONStorage(() => sessionStorage),
  wasCheapOfferBought: false,
  wasStandardPlanBought: false,
  paymentMethod: '',
  paymentSystem: '',
  subscriptionId: '',
  productId: '',
  btSubscriptionId: '',
  btCustomerId: '',
  wasSubscriptionModified: false,
});

export const stripeSubscription = atom(
  typeof window !== 'undefined' && (sessionStorage.getItem('myKey') ?? ''),
);

export const paymentMethod = atom(
  typeof window !== 'undefined' &&
    (sessionStorage.getItem('btPaymentMethod') ?? ''),
);

export const stripeSubscriptionWithPersistence = atomWithStorage<string>(
  'myKey',
  stripeSubscription as unknown as string,
);

export const paymentMethodWithPersistence = atom(
  (get) => get(paymentMethod),
  (get, set, newString: string) => {
    set(paymentMethod, newString);
    sessionStorage.setItem('btPaymentMethod', newString);
  },
);

export const selectedPaymentMethodTabAtom = atom<{
  paymentMethod:
    | 'PayPal'
    | 'Google Pay'
    | 'Apple Pay'
    | 'Credit card'
    | undefined;
}>({ paymentMethod: undefined });
